import React, { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";

/**
 * Minimal dependency-free country-code picker. Shows a flag emoji + dial code
 * in a dropdown next to the mobile input so international users can pick any
 * ISO country instead of being forced onto +91.
 *
 * Usage:
 *   <CountryCodePicker value={cc} onChange={setCc} />
 *   value: object {code, dial, flag, name}. Default = India (+91).
 */

export const COUNTRIES = [
  { code: "IN", dial: "+91", flag: "🇮🇳", name: "India" },
  { code: "US", dial: "+1",  flag: "🇺🇸", name: "United States" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "AE", dial: "+971", flag: "🇦🇪", name: "United Arab Emirates" },
  { code: "SG", dial: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "CA", dial: "+1",  flag: "🇨🇦", name: "Canada" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "SA", dial: "+966", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "MY", dial: "+60", flag: "🇲🇾", name: "Malaysia" },
  { code: "NZ", dial: "+64", flag: "🇳🇿", name: "New Zealand" },
  { code: "ZA", dial: "+27", flag: "🇿🇦", name: "South Africa" },
  { code: "NP", dial: "+977", flag: "🇳🇵", name: "Nepal" },
  { code: "BD", dial: "+880", flag: "🇧🇩", name: "Bangladesh" },
  { code: "LK", dial: "+94", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "PK", dial: "+92", flag: "🇵🇰", name: "Pakistan" },
  { code: "PH", dial: "+63", flag: "🇵🇭", name: "Philippines" },
  { code: "ID", dial: "+62", flag: "🇮🇩", name: "Indonesia" },
  { code: "JP", dial: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "KR", dial: "+82", flag: "🇰🇷", name: "South Korea" },
  { code: "CN", dial: "+86", flag: "🇨🇳", name: "China" },
  { code: "HK", dial: "+852", flag: "🇭🇰", name: "Hong Kong" },
  { code: "TH", dial: "+66", flag: "🇹🇭", name: "Thailand" },
  { code: "VN", dial: "+84", flag: "🇻🇳", name: "Vietnam" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Netherlands" },
  { code: "SE", dial: "+46", flag: "🇸🇪", name: "Sweden" },
  { code: "CH", dial: "+41", flag: "🇨🇭", name: "Switzerland" },
  { code: "IE", dial: "+353", flag: "🇮🇪", name: "Ireland" },
  { code: "BE", dial: "+32", flag: "🇧🇪", name: "Belgium" },
  { code: "AT", dial: "+43", flag: "🇦🇹", name: "Austria" },
  { code: "DK", dial: "+45", flag: "🇩🇰", name: "Denmark" },
  { code: "NO", dial: "+47", flag: "🇳🇴", name: "Norway" },
  { code: "FI", dial: "+358", flag: "🇫🇮", name: "Finland" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Poland" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Türkiye" },
  { code: "EG", dial: "+20", flag: "🇪🇬", name: "Egypt" },
  { code: "KE", dial: "+254", flag: "🇰🇪", name: "Kenya" },
  { code: "NG", dial: "+234", flag: "🇳🇬", name: "Nigeria" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "Mexico" },
  { code: "AR", dial: "+54", flag: "🇦🇷", name: "Argentina" },
  { code: "RU", dial: "+7",  flag: "🇷🇺", name: "Russia" },
  { code: "IL", dial: "+972", flag: "🇮🇱", name: "Israel" },
  { code: "QA", dial: "+974", flag: "🇶🇦", name: "Qatar" },
  { code: "KW", dial: "+965", flag: "🇰🇼", name: "Kuwait" },
  { code: "OM", dial: "+968", flag: "🇴🇲", name: "Oman" },
  { code: "BH", dial: "+973", flag: "🇧🇭", name: "Bahrain" },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]; // India

/** Given a raw phone string ("+9198…" / "9198…" / "98…") return {country, national}. */
export const guessCountryFromPhone = (raw) => {
  if (!raw) return { country: DEFAULT_COUNTRY, national: "" };
  const digits = String(raw).replace(/[^\d+]/g, "");
  const withPlus = digits.startsWith("+") ? digits : (digits.startsWith("00") ? "+" + digits.slice(2) : "+" + digits);
  // Longest-prefix match on dial code
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const hit = sorted.find((c) => withPlus.startsWith(c.dial));
  if (hit) return { country: hit, national: withPlus.slice(hit.dial.length) };
  return { country: DEFAULT_COUNTRY, national: withPlus.replace(/^\+/, "") };
};

const CountryCodePicker = ({ value, onChange, disabled }) => {
  const active = value || DEFAULT_COUNTRY;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(term) ||
      c.dial.includes(term) ||
      c.code.toLowerCase().includes(term)
    );
  }, [q]);

  // Click-away to close
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="h-12 pl-3 pr-2 flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-l-xl border-r-0 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        data-testid="country-code-btn"
        aria-label="Select country code"
      >
        <span className="text-lg leading-none">{active.flag}</span>
        <span className="tabular-nums">{active.dial}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-white border rounded-xl shadow-xl z-30" data-testid="country-code-dropdown">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                autoFocus
                placeholder="Search country or code…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-sm bg-slate-50 border rounded-md focus:outline-none focus:border-indigo-400"
                data-testid="country-code-search"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange?.(c); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-indigo-50 ${
                  c.code === active.code ? "bg-indigo-50 font-medium" : ""
                }`}
                data-testid={`country-code-option-${c.code}`}
              >
                <span className="text-lg leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="tabular-nums text-slate-500">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-500 text-center">No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodePicker;
